import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

class TermsOfService extends BaseComponent {
  static styles = css`
    :host {
      display: block;
    }

    h2 {
      font-size: 1.25rem;
      margin-top: 1.5rem;
    }

    h3 {
      font-size: 1.1rem;
      margin-top: 1rem;
    }

    ul {
      padding-left: 1.5rem;
    }

    .flash-target {
      animation: flash-highlight 1.8s ease-out;
    }

    @keyframes flash-highlight {
      0% {
        background-color: rgba(255, 235, 59, 0.6);
        box-shadow: 0 0 0 0.5rem rgba(255, 235, 59, 0.6);
      }
      100% {
        background-color: transparent;
        box-shadow: 0 0 0 0.5rem transparent;
      }
    }
  `;

  firstUpdated() {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    requestAnimationFrame(() => {
      const target = this.shadowRoot?.getElementById(hash);
      if (!target) return;
      target.scrollIntoView({ block: 'start' });
      target.classList.add('flash-target');
      target.addEventListener('animationend', () => target.classList.remove('flash-target'), { once: true });
    });
  }

  render() {
    return html`
      <p><strong>Last Updated: May 2026</strong></p>
      <p><strong>IMPORTANT: PLEASE READ THESE TERMS CAREFULLY BEFORE USING RPGISH.</strong></p>
      <p>
        These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your")
        and Dice Driven Design LLC ("Company," "we," "us," or "our") governing your access to and use of the RPGish
        platform, including any associated websites, applications, and services (collectively, the "Service").
      </p>
      <p>
        By creating an account or using the Service, you acknowledge that you have read, understood, and agree to be
        bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.
      </p>

      <h2>1. Eligibility</h2>
      <h3>1.1 Age Requirement</h3>
      <p>
        RPGish is intended for users who are 13 years of age or older. By creating an account or using RPGish, you
        represent and warrant that you are at least 13 years old.
      </p>
      <p>
        If you are under 13 years of age, you may not create an account or use RPGish. We do not knowingly collect
        personal information from children under 13. If we become aware that we have collected personal information from
        a child under 13 without verification of parental consent, we will delete that information promptly.
      </p>
      <p>
        If you believe we have inadvertently collected information from a child under 13, please contact us immediately
        at privacy@rpgish.com.
      </p>
      <p>
        If you are between 13 and 18 years of age (or the age of legal majority in your jurisdiction), you may only use
        the Service with the consent of a parent or legal guardian who agrees to be bound by these Terms.
      </p>

      <h3>1.2 Account Registration</h3>
      <p>To access certain features of the Service, you must register for an account. You agree to:</p>
      <ul>
        <li>Provide accurate, current, and complete information during registration</li>
        <li>Maintain and promptly update your account information</li>
        <li>Maintain the security of your password and accept responsibility for all activities under your account</li>
        <li>Immediately notify us of any unauthorized use of your account</li>
      </ul>

      <h2>2. Description of Service</h2>
      <h3>2.1 Platform Overview</h3>
      <p>
        RPGish is a tabletop role-playing game platform that provides automated, Game Master-less (GM-less) RPG
        gameplay. The Service allows users to play structured campaigns solo or with friends through a web-based
        interface.
      </p>

      <h3>2.2 Game System</h3>
      <p>
        The Service utilizes game mechanics based on the 5th Edition System Reference Document (SRD 5.2.1), which is
        available under the Creative Commons Attribution 4.0 International License. The Service is compatible with
        fifth edition.
      </p>

      <h3>2.3 Beta/Playtest Status</h3>
      <p>
        The Service is currently in beta/playtest status. Features may be incomplete, subject to change, or removed
        without notice. Your use of the Service during this period is also governed by the Playtest Non-Disclosure
        Agreement.
      </p>

      <h2>3. User Conduct</h2>
      <h3>3.1 Acceptable Use</h3>
      <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</p>
      <ul>
        <li>Use the Service in any way that violates applicable laws or regulations</li>
        <li>Impersonate any person or entity or misrepresent your affiliation</li>
        <li>
          Engage in conduct that is harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable
        </li>
        <li>
          Attempt to gain unauthorized access to any portion of the Service or any systems or networks connected to the
          Service
        </li>
        <li>Use any automated means to access the Service or collect information from the Service</li>
        <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
        <li>Upload or transmit viruses, malware, or other malicious code</li>
      </ul>

      <h3>3.2 User-Generated Content</h3>
      <p>
        If the Service allows you to create, upload, or share content (such as character names, adventure content, or
        communications with other users), you agree that such content will not:
      </p>
      <ul>
        <li>Infringe any third-party intellectual property rights</li>
        <li>Contain illegal, obscene, or offensive material</li>
        <li>Contain personal information of others without their consent</li>
        <li>Violate any applicable laws or regulations</li>
      </ul>

      <h2>4. Intellectual Property</h2>
      <h3>4.1 Company Property</h3>
      <p>
        The Service, including its original content, features, and functionality, is owned by Dice Driven Design LLC and
        is protected by copyright, trademark, and other intellectual property laws. Our trademarks and trade dress may
        not be used without our prior written permission.
      </p>

      <h3>4.2 User Content License</h3>
      <p>
        By submitting content to the Service, you grant us a worldwide, non-exclusive, royalty-free, sublicensable, and
        transferable license to use, reproduce, modify, adapt, publish, translate, and distribute such content in
        connection with the Service.
      </p>

      <section id="open-game-content">
        <h3>4.3 Open Game Content</h3>
        <p>
          This work includes material from the System Reference Document 5.2.1 ("SRD 5.2.1") by Wizards of the Coast
          LLC, available at
          <a href="https://www.dndbeyond.com/srd" target="_blank" rel="noopener">https://www.dndbeyond.com/srd</a>. The
          SRD 5.2.1 is licensed under the Creative Commons Attribution 4.0 International License, available at
          <a href="https://creativecommons.org/licenses/by/4.0/legalcode" target="_blank" rel="noopener"
            >https://creativecommons.org/licenses/by/4.0/legalcode</a
          >.
        </p>
      </section>

      <h3>4.4 Feedback</h3>
      <p>
        Any feedback, suggestions, or ideas you provide regarding the Service ("Feedback") will be owned by the Company.
        You hereby assign all rights in such Feedback to the Company and agree that we may use Feedback for any purpose
        without compensation to you.
      </p>

      <h2>5. Privacy</h2>
      <p>
        Your privacy is important to us. Our collection and use of personal information is governed by our Privacy
        Policy, which is incorporated into these Terms by reference.
      </p>

      <h2>6. Playtest Program</h2>
      <h3>6.1 Purpose</h3>
      <p>
        The Service is currently offered as a limited playtest to gather feedback, identify bugs, and improve the
        gameplay experience before public release. Your participation helps us develop and refine RPGish.
      </p>

      <h3>6.2 Feedback and Bug Reports</h3>
      <p>
        We encourage you to submit feedback, suggestions, and bug reports through our official Discord server at
        discord.gg/rpgish. All feedback submitted becomes the property of the Company as described in Section 4.4
        (Feedback) and the Playtest Non-Disclosure Agreement.
      </p>

      <h3>6.3 Data and Progress</h3>
      <p>You acknowledge and agree that:</p>
      <ul>
        <li>
          Your characters, game progress, saved data, and other gameplay information may be reset, modified, or deleted
          at any time during the Playtest
        </li>
        <li>Data from the Playtest may not transfer to the final release version of RPGish</li>
        <li>We may need to wipe all user data for technical, balancing, or business reasons without prior notice</li>
        <li>You should not rely on the persistence of any data created during the Playtest</li>
      </ul>

      <h3>6.4 Playtest Termination</h3>
      <p>
        The Company may terminate the Playtest program at any time, with or without notice. Upon termination of the
        Playtest:
      </p>
      <ul>
        <li>Your access to the Playtest version of the Service will end</li>
        <li>All Playtest data may be deleted</li>
        <li>You will be notified of any transition to a public release and any new terms that may apply</li>
      </ul>

      <h2>7. Disclaimers</h2>
      <h3>7.1 "As Is" Basis</h3>
      <p>
        THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
        IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
        TITLE, AND NON-INFRINGEMENT.
      </p>

      <h3>7.2 No Guarantee</h3>
      <p>
        We do not warrant that the Service will be uninterrupted, secure, or error-free, that defects will be corrected,
        or that the Service is free of viruses or other harmful components.
      </p>

      <h3>7.3 Beta Disclaimer</h3>
      <p>
        As a beta/playtest service, you acknowledge that the Service may contain bugs, errors, and other problems that
        could cause system failures. Your use of the Service is at your own risk.
      </p>

      <h2>8. Limitation of Liability</h2>
      <h3>8.1 Exclusion of Damages</h3>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL DICE DRIVEN DESIGN LLC, ITS DIRECTORS, EMPLOYEES,
        PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
        PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE
        LOSSES.
      </p>

      <h3>8.2 Liability Cap</h3>
      <p>
        OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE SHALL NOT
        EXCEED THE GREATER OF: (A) THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE
        HUNDRED DOLLARS ($100).
      </p>

      <h3>8.3 Basis of Bargain</h3>
      <p>
        THE LIMITATIONS OF LIABILITY SET FORTH ABOVE ARE FUNDAMENTAL ELEMENTS OF THE BASIS OF THE BARGAIN BETWEEN THE
        COMPANY AND YOU.
      </p>

      <h2>9. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless Dice Driven Design LLC and its officers, directors, employees,
        agents, and affiliates from and against any claims, liabilities, damages, losses, and expenses, including
        reasonable attorneys' fees, arising out of or in any way connected with your access to or use of the Service,
        your violation of these Terms, or your violation of any third-party rights.
      </p>

      <h2>10. Termination</h2>
      <h3>10.1 By You</h3>
      <p>
        You may terminate your account at any time by contacting us at privacy@rpgish.com to request account deletion.
      </p>

      <h3>10.2 By Us</h3>
      <p>
        We may suspend or terminate your access to the Service at any time, with or without cause, and with or without
        notice. Upon termination, your right to use the Service will immediately cease.
      </p>

      <h3>10.3 Effect of Termination</h3>
      <p>
        Provisions of these Terms that by their nature should survive termination shall survive, including ownership
        provisions, warranty disclaimers, indemnification, and limitations of liability.
      </p>

      <h2>11. Governing Law and Dispute Resolution</h2>
      <h3>11.1 Governing Law</h3>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the State of Washington, United
        States, without regard to its conflict of law provisions.
      </p>

      <h3>11.2 Jurisdiction</h3>
      <p>
        You agree to submit to the personal and exclusive jurisdiction of the courts located in King County, Washington,
        United States, for the resolution of any disputes arising out of or relating to these Terms or the Service.
      </p>

      <h3>11.3 Waiver of Jury Trial</h3>
      <p>
        YOU AND THE COMPANY WAIVE ANY CONSTITUTIONAL AND STATUTORY RIGHTS TO GO TO COURT AND HAVE A TRIAL IN FRONT OF A
        JUDGE OR A JURY.
      </p>

      <h2>12. International Users</h2>
      <p>
        The Service is operated from the United States. If you access the Service from outside the United States, you do
        so at your own risk and are responsible for compliance with local laws. By using the Service, you consent to the
        transfer of your information to the United States.
      </p>

      <h2>13. Changes to Terms</h2>
      <p>
        We reserve the right to modify these Terms at any time. If we make material changes, we will notify you by email
        or by posting a notice on the Service prior to the effective date of the changes. Your continued use of the
        Service after the effective date constitutes your acceptance of the modified Terms.
      </p>

      <h2>14. General Provisions</h2>
      <h3>14.1 Entire Agreement</h3>
      <p>
        These Terms, together with the Privacy Policy and Playtest NDA, constitute the entire agreement between you and
        the Company regarding the Service.
      </p>

      <h3>14.2 Severability</h3>
      <p>
        If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full
        force and effect.
      </p>

      <h3>14.3 Waiver</h3>
      <p>
        Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or
        provision.
      </p>

      <h3>14.4 Assignment</h3>
      <p>
        You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may
        assign these Terms without restriction.
      </p>

      <h2>15. Contact Information</h2>
      <p>If you have any questions about these Terms, please contact us at:</p>
      <p><strong>Dice Driven Design LLC</strong><br />Email: legal@rpgish.com</p>
    `;
  }
}

customElements.define('terms-of-service', TermsOfService);
